import { useCallback, useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';

/**
 * Extra metrics for the control-centre hero tiles that no existing hook
 * provides: total invoiced this month, and a monthly close-rate series for
 * the sparkline (share of leads created each month that are now Won).
 */

function monthStartsBack(count: number): Date[] {
  const now = new Date();
  const starts: Date[] = [];
  for (let i = count - 1; i >= 0; i--) {
    starts.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
  }
  return starts;
}

export function useInvoicedThisMonth() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const [invoicedTotal, setInvoicedTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setInvoicedTotal(0);
      setLoading(false);
      return;
    }
    try {
      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      // Issued invoices only — drafts aren't billed yet, voids don't count.
      const { data, error } = await supabase
        .from('invoices')
        .select('total_cents')
        .eq('business_id', businessId)
        .gte('created_at', monthStart.toISOString())
        .not('status', 'in', '(draft,void)');
      if (error) throw error;
      const cents = (data || []).reduce((sum, r) => sum + (Number(r.total_cents) || 0), 0);
      setInvoicedTotal(cents / 100);
    } catch (err) {
      console.error('Error loading invoiced-this-month total:', err);
      setInvoicedTotal(0);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { invoicedTotal, loading, refresh: load };
}

export interface CloseRatePoint {
  /** Short month label, e.g. "Jun". */
  label: string;
  /** 0–100 close rate for leads created that month. */
  rate: number;
}

export function useCloseRateTrend(enabled: boolean, months = 4) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const [points, setPoints] = useState<CloseRatePoint[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!supabase || !businessId || !enabled) {
      setPoints([]);
      setLoading(false);
      return;
    }
    try {
      const starts = monthStartsBack(months);
      const rangeStart = starts[0];
      // Cohort view (same definition as the CRM close-rate donut): of the
      // leads CREATED in each month, how many are Won now.
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .select('created_at, status')
        .eq('business_id', businessId)
        .not('created_at', 'is', null)
        .gte('created_at', rangeStart.toISOString());
      if (error) throw error;

      const buckets = starts.map((start, i) => {
        const end = i + 1 < starts.length
          ? starts[i + 1]
          : new Date(start.getFullYear(), start.getMonth() + 1, 1);
        return { start, end, created: 0, won: 0 };
      });
      for (const row of data || []) {
        const created = new Date(row.created_at as string);
        const bucket = buckets.find((b) => created >= b.start && created < b.end);
        if (!bucket) continue;
        bucket.created += 1;
        if (String(row.status) === 'Won') bucket.won += 1;
      }
      setPoints(
        buckets.map((b) => ({
          label: b.start.toLocaleDateString('en-US', { month: 'short' }),
          rate: b.created > 0 ? Math.round((b.won / b.created) * 100) : 0,
        }))
      );
    } catch (err) {
      console.error('Error loading close-rate trend:', err);
      setPoints([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, enabled, months]);

  useEffect(() => {
    void load();
  }, [load]);

  return { points, loading, refresh: load };
}
